'use client';

/**
 * Infiles.tsx — produktside for inFiles (`/infiles`)
 *
 * Følger ibm.com/products-mønsteret, som /webhotell: brødsmulesti og hero i
 * grått bånd, fast seksjonsmeny, oversikt, funksjoner (akkordion), alpha-promo,
 * nummererte steg, for hvem, spørsmål og avsluttende CTA. Komponentene ligger i
 * src/components/product/ og deles med Webhotell. Header, footer, knapper,
 * språkvalg og tema deles med forsiden. Ingen UI Shell (ROUTES_WITHOUT_SHELL
 * i ClientUiShell.tsx).
 *
 * Innhold: statisk, norsk + engelsk, se infiles-content.ts.
 */

import ClosingCta from '@/components/landing/ClosingCta';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingHeader from '@/components/landing/LandingHeader';
import { MASTHEAD_CONTENT } from '@/components/landing/masthead-content';
import InfilesHeroIllustration from '@/components/infiles/InfilesHeroIllustration';
import InfilesPanelIllustration from '@/components/infiles/InfilesPanelIllustration';
import FaqAccordion from '@/components/product/FaqAccordion';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import NumberedSteps from '@/components/product/NumberedSteps';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import UseCases from '@/components/product/UseCases';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import landingStyles from '@/sections/landing/landing.module.css';
import { INFILES_CONTENT } from './infiles-content';

export default function Infiles() {
  const { locale, setLocale } = useLocale();
  const content = INFILES_CONTENT[locale];

  return (
    <div className={landingStyles.page}>
      <LandingHeader ui={content.ui} menu={MASTHEAD_CONTENT[locale]} locale={locale} onLocaleChange={setLocale} />
      <main id="innhold" className={landingStyles.main}>
        <ProductHero
          hero={content.hero}
          breadcrumb={content.breadcrumb}
          badge={content.hero.badge}
          illustration={<InfilesHeroIllustration label={content.hero.illustrationLabel} />}
        />

        <SectionNav nav={content.sectionNav} />

        <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
          <div className={styles.container}>
            <SectionHeader
              id="overview-heading"
              eyebrow={content.overview.eyebrow}
              heading={content.overview.heading}
              blue
            >
              {content.overview.body.map((paragraph) => (
                <p key={paragraph} className={styles.lead}>
                  {paragraph}
                </p>
              ))}
            </SectionHeader>
          </div>
        </section>

        <FeatureAccordion
          features={content.features}
          art={<InfilesPanelIllustration label={content.features.panelLabel} variant="files" />}
        />
        <PromoBanner promo={content.promo} />
        <NumberedSteps
          id="steg"
          steps={content.steps}
          art={<InfilesPanelIllustration label={content.steps.panelLabel} variant="chip" />}
        />
        <UseCases useCases={content.audiences} id="for-hvem" />
        <FaqAccordion faq={content.faq} />
        <ClosingCta cta={content.cta} />
      </main>
      <LandingFooter
        tagline={content.footer.tagline}
        legal={content.footer.legal}
        columns={[
          {
            heading: content.footer.platformLabel,
            links: [
              { label: 'inSuite', href: '/' },
              { label: content.ui.login, href: '/login' },
            ],
          },
          {
            heading: content.footer.sectionsLabel,
            links: content.sectionNav.items.map((item) => ({ label: item.label, href: `#${item.id}` })),
          },
        ]}
      />
    </div>
  );
}
